import { gql, useLazyQuery } from "@apollo/client";
import { Analytics, Auth } from "aws-amplify";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  WBAlert,
  WBBox,
  WBCard,
  WBFlex,
  WBTypography,
} from "@apptractive/design-system-web";
import { useLocation, useNavigate } from "react-router-dom";
import { OnboardingStatus } from "../../API";
import { Link } from "../../components/Link";
import { isLoggedInVar, subInVar } from "../../customGraphql/cache";
import { getOnboardingPath } from "../../helpers/onboarding";
import { PATHS } from "../../navigation/paths";
import { AuthError, RequestStatus } from "@apptractive/stm-shared";
import ChangePasswordForm, {
  ChangePasswordFormData,
} from "../ChangePassword/ChangePasswordForm";
import { SignUpFormData } from "../SignUp/SignUpForm";
import SignInForm from "./SignInForm";
import { getUser as GET_USER } from "../../graphql/queries";

interface SignInFormData {
  email: string;
  password: string;
}

const SignIn = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location: any = useLocation();
  const [isNewPasswordRequired, setIsNewPasswordRequired] =
    useState<boolean>(false);
  const [codeDelivery, setCodeDelivery] = useState<string>("");
  const [authUser, setAuthUser] = useState<any>({});
  const [authStatus, setAuthStatus] = useState<RequestStatus>("idle");
  const [authError, setAuthError] = useState<AuthError>({});
  const [submittedEmail, setSubmittedEmail] = useState("");
  const [submittedPassword, setSubmittedPassword] = useState<string>("");

  const [getUser, { error: userError }] = useLazyQuery(gql(GET_USER));

  const from: string = useMemo(
    () => location.state?.from?.pathname || null,
    [location]
  );

  useEffect(() => {
    isLoggedInVar(false);
    localStorage.removeItem("sub");
  }, []);

  /**
   * Sign in a user
   *
   * @param data
   */
  const onSignIn = async (data: SignInFormData) => {
    setAuthStatus("submitting");
    const { email, password } = data;

    const params = {
      username: email!.toLowerCase().trim(),
      password,
    };

    let cognitoUser;
    try {
      cognitoUser = await Auth.signIn(params);
    } catch (err: any) {
      console.log("ERROR sign in: ", err.code);
      setAuthError(err);
      setAuthStatus("error");

      if (err.code === "UserNotConfirmedException") {
        await resendCode({ email });
      }
    }

    if (cognitoUser) {
      localStorage.setItem("sub", cognitoUser.username as string);

      // update analytics userId
      const options = {
        userId: cognitoUser.username,
      };
      try {
        await Analytics.updateEndpoint(options);
      } catch (err) {
        console.log("ERROR update endpoint: ", err);
      }

      let loggedInUserData;
      let loggedInUser;
      if (cognitoUser?.challengeName !== "NEW_PASSWORD_REQUIRED") {
        try {
          loggedInUserData = await getUser({
            variables: {
              id: cognitoUser.username,
              skip: cognitoUser?.challengeName === "NEW_PASSWORD_REQUIRED",
            },
          });
        } catch (err) {
          console.log("ERROR getUser", err);
        }
      }

      loggedInUser = loggedInUserData?.data?.getUser;

      setAuthStatus("success");
      if (cognitoUser?.challengeName === "NEW_PASSWORD_REQUIRED") {
        setAuthUser(cognitoUser);
        setSubmittedPassword(password);
        setIsNewPasswordRequired(true);
      } else if (
        loggedInUser?.onboardingStatus !== OnboardingStatus.COMPLETED
      ) {
        isLoggedInVar(true);
        subInVar(cognitoUser.username);
        navigate(getOnboardingPath(loggedInUser), { replace: true });
      } else if (from) {
        isLoggedInVar(true);
        subInVar(cognitoUser.username);
        // redirect to intended page if set and is logged in
        navigate(from, { replace: true });
      } else {
        isLoggedInVar(true);
        subInVar(cognitoUser.username);
        // redirect to dashboard if logged in
        navigate(getOnboardingPath(loggedInUser), { replace: true });
      }
    }
  };

  const onSubmitNewPassword = async ({
    newPassword,
  }: ChangePasswordFormData) => {
    setAuthStatus("submitting");
    let cognitoUser;
    try {
      cognitoUser = await Auth.completeNewPassword(authUser, newPassword);

      // at this time the user is logged in if no MFA required
      localStorage.setItem("sub", authUser.username as string);

      let loggedInUserData;
      let loggedInUser;
      try {
        loggedInUserData = await getUser({
          variables: {
            id: cognitoUser.username,
          },
        });

        loggedInUser = loggedInUserData?.data?.getUser;
      } catch (err) {
        console.log("ERROR getUser", err);
      }
      isLoggedInVar(true);
      subInVar(authUser.username);
      navigate(getOnboardingPath(loggedInUser), { replace: true });
    } catch (err: any) {
      console.log("ERROR set new password: ", err);
      setAuthError(err);
      setAuthStatus("error");
    }
  };

  /**
   * Resend verification code for sign in
   */
  const resendCode = async ({ email }: { email: string }) => {
    setSubmittedEmail(email.toLowerCase().trim());

    try {
      const {
        CodeDeliveryDetails: { DeliveryMedium },
      } = await Auth.resendSignUp(email.toLowerCase().trim());
      setCodeDelivery(DeliveryMedium);
    } catch (err: any) {
      setAuthError(err);
      setAuthStatus("error");
    }

    alert(t("unverifiedEmailSendCode", { ns: "common" }));
  };

  const onConfirmSignUp = async ({ email, password, code }: SignUpFormData) => {
    setAuthStatus("submitting");

    try {
      await Auth.confirmSignUp(email, code);
    } catch (err: any) {
      setAuthError(err);
      setAuthStatus("error");
    }

    try {
      const params = {
        username: email.toLowerCase().trim(),
        password,
      };

      const user = await Auth.signIn(params);

      localStorage.setItem("sub", user.username as string);
      isLoggedInVar(true);
      subInVar(user.username);
      // redirect to dashboard if logged in
      navigate(PATHS.onboardingUser, { replace: true });
    } catch (err: any) {
      console.log("ERROR sign in: ", err);
      setAuthError(err);
      setAuthStatus("error");
    }
  };

  return (
    <WBFlex justifyContent="center" alignItems="center" flex={1}>
      <WBCard
        sx={{
          width: ["100%", "100%", "50%"],
          backgroundColor: "background.paper",
          boxShadow: 5,
          borderRadius: 1,
          padding: 5,
        }}
      >
        <WBTypography variant="h2" color="textSecondary">
          {codeDelivery
            ? t("verificationSent", { ns: "common" })
            : t("welcomeBack", { ns: "signIn" })}
        </WBTypography>

        <WBFlex flexDirection="row" flexWrap="wrap">
          {codeDelivery && !isNewPasswordRequired && (
            <WBTypography>
              {t("enterVerificationCodeSent", { ns: "common" })}{" "}
              {submittedEmail}
            </WBTypography>
          )}

          {!codeDelivery && isNewPasswordRequired && (
            <WBTypography>
              {t("newPasswordContinue", { ns: "common" })}
            </WBTypography>
          )}

          {!codeDelivery && !isNewPasswordRequired && (
            <WBTypography color="textSecondary">
              {t("noAccount", { ns: "signIn" })}{" "}
              <Link to={PATHS.signUp}>
                {t("signUpTitle", { ns: "common" })}
              </Link>
            </WBTypography>
          )}
        </WBFlex>

        {!isNewPasswordRequired && (
          <SignInForm
            authError={authError}
            authStatus={authStatus}
            codeDelivery={codeDelivery}
            onSignIn={onSignIn}
            onConfirmSignUp={onConfirmSignUp}
          />
        )}

        {isNewPasswordRequired && (
          <ChangePasswordForm
            authError={authError}
            authStatus={authStatus}
            oldPassword={submittedPassword}
            onSubmit={onSubmitNewPassword}
          />
        )}

        {userError?.message && (
          <WBAlert title={userError.message} severity="error" />
        )}

        <WBFlex
          flexDirection="row"
          flexWrap="wrap"
          flex={1}
          justifyContent="center"
        >
          <WBBox mt={3}>
            <Link to={PATHS.resetPassword}>
              {t("forgotPasswordTitle", { ns: "common" })}
            </Link>
          </WBBox>
        </WBFlex>
      </WBCard>
    </WBFlex>
  );
};

export default SignIn;
